import React from "react";
import { HexColorPicker } from "react-colorful";

/*
  쇼핑몰 디자인 설정 패널 공통 UI (2026-10-26 정리).
  카드마다 따로 그리던 제목 줄 · 사용 토글 · 버튼형 선택 · 색상 입력 · 안내 문구를 한 모양으로 맞춘다.
  토글은 모두 스위치(체크박스 혼용 없음), 선택지는 모두 세그먼트 버튼이다.
*/

interface ToggleSwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  size?: "sm" | "md";
}

export const ToggleSwitch = ({ checked, onChange, label, size = "md" }: ToggleSwitchProps) => {
  const track = size === "sm" ? "w-[30px] h-[18px]" : "w-[38px] h-[22px]";
  const knob = size === "sm" ? "w-[14px] h-[14px]" : "w-[18px] h-[18px]";
  const shift = size === "sm" ? "translate-x-[12px]" : "translate-x-[16px]";
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={(event) => {
        event.stopPropagation();
        onChange(!checked);
      }}
      className={`relative shrink-0 ${track} rounded-full transition-colors ${checked ? "bg-[#4285F4]" : "bg-[#D1D5DB]"}`}
    >
      <span
        className={`absolute top-[2px] left-[2px] ${knob} rounded-full bg-white shadow-sm transition-transform ${checked ? shift : ""}`}
      />
    </button>
  );
};

interface SettingsCardProps {
  title: string;
  description?: React.ReactNode;
  /** 제목 오른쪽 보조 정보 (개수 · 상태 등) */
  aside?: React.ReactNode;
  /** 있으면 제목 줄에 사용 스위치를 두고, 꺼져 있으면 본문을 접는다 */
  toggle?: { checked: boolean; onChange: (checked: boolean) => void; label?: string };
  children?: React.ReactNode;
}

export const SettingsCard = ({ title, description, aside, toggle, children }: SettingsCardProps) => {
  const isOpen = !toggle || toggle.checked;
  return (
    <section className="w-full text-start bg-white border border-[#E5E7EB] rounded-[10px] p-[1rem] mb-[0.75rem]">
      <div className="flex flex-row items-center gap-[0.5rem]">
        <h3 className="text-[15px] font-bold text-[#252525]">{title}</h3>
        {aside ? <div className="text-[12px] text-[#888]">{aside}</div> : null}
        {toggle ? (
          <div className="ml-auto flex flex-row items-center gap-[0.5rem]">
            <span className="text-[12px] text-[#888]">{toggle.checked ? "사용" : "사용 안 함"}</span>
            <ToggleSwitch checked={toggle.checked} onChange={toggle.onChange} label={toggle.label ?? `${title} 사용`} />
          </div>
        ) : null}
      </div>
      {description ? <p className="mt-[0.25rem] text-[12px] leading-[1.6] text-[#888]">{description}</p> : null}
      {isOpen && children ? <div className="mt-[0.875rem] flex flex-col gap-[0.875rem]">{children}</div> : null}
    </section>
  );
};

interface FieldProps {
  label: string;
  hint?: React.ReactNode;
  children: React.ReactNode;
}

export const Field = ({ label, hint, children }: FieldProps) => (
  <div className="flex flex-col gap-[0.375rem]">
    <div className="text-[12px] font-medium text-[#555]">{label}</div>
    {children}
    {hint ? <div className="text-[12px] leading-[1.5] text-[#999]">{hint}</div> : null}
  </div>
);

interface SegmentedProps<T extends string | number> {
  value: T;
  options: { value: T; label: React.ReactNode }[];
  onChange: (value: T) => void;
  ariaLabel: string;
}

export const Segmented = <T extends string | number>({ value, options, onChange, ariaLabel }: SegmentedProps<T>) => (
  <div role="radiogroup" aria-label={ariaLabel} className="flex flex-row gap-[0.25rem] p-[0.25rem] bg-[#F3F4F6] rounded-[8px]">
    {options.map((option) => {
      const selected = option.value === value;
      return (
        <button
          key={String(option.value)}
          type="button"
          role="radio"
          aria-checked={selected}
          onClick={() => onChange(option.value)}
          className={`flex-1 min-w-0 h-[2rem] px-[0.5rem] rounded-[6px] text-[13px] transition-colors ${
            selected ? "bg-white text-[#4285F4] font-bold shadow-[0_1px_2px_rgba(0,0,0,0.12)]" : "text-[#555] hover:text-[#252525]"
          }`}
        >
          {option.label}
        </button>
      );
    })}
  </div>
);

export const TextField = (props: React.InputHTMLAttributes<HTMLInputElement>) => (
  <input
    {...props}
    className={`w-full min-w-0 h-[2.25rem] px-[0.75rem] border border-[#E0E0E0] rounded-[8px] text-[13px] bg-white
      placeholder:text-[#BBBBBB] focus:border-[#4285F4] focus:outline-none ${props.className ?? ""}`}
  />
);

export const SelectField = (props: React.SelectHTMLAttributes<HTMLSelectElement>) => (
  <select
    {...props}
    className={`w-full min-w-0 h-[2.25rem] px-[0.5rem] border border-[#E0E0E0] rounded-[8px] text-[13px] bg-white
      focus:border-[#4285F4] focus:outline-none ${props.className ?? ""}`}
  />
);

interface ColorFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  /** 팝오버를 칸 왼쪽(start) · 오른쪽(end) 끝에 맞춘다. 2열 그리드 오른쪽 칸은 end 로 둬야 패널 밖으로 안 나간다 */
  popoverAlign?: "start" | "end";
}

const HEX_PATTERN = /^#[0-9a-fA-F]{6}$/;

/** 팝오버 아래 빠른 선택 색 (흰 · 검정 계열 · MES 브랜드 블루) */
const QUICK_COLORS = ["#FFFFFF", "#F5F8FF", "#F3F4F6", "#252525", "#555555", "#4285F4", "#2C5AA0", "#111827"];

/** 색 칩 + HEX 입력을 한 칸에 두고, 칩을 누르면 react-colorful 팝오버를 연다 (브라우저 기본 피커 대신) */
export const ColorField = ({ label, value, onChange, popoverAlign = "start" }: ColorFieldProps) => {
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const isValid = HEX_PATTERN.test(value);
  const safeColor = isValid ? value : "#000000";

  React.useEffect(() => {
    if (!open) return;
    const handlePointer = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handlePointer);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handlePointer);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  return (
    <Field label={label}>
      <div ref={rootRef} className="relative">
        <div
          className={`flex flex-row items-center gap-[0.5rem] h-[2.25rem] pl-[0.3125rem] pr-[0.75rem] border rounded-[8px] bg-white transition-colors ${
            !isValid ? "border-[#E5484D]" : open ? "border-[#4285F4]" : "border-[#E0E0E0] focus-within:border-[#4285F4]"
          }`}
        >
          <button
            type="button"
            aria-label={`${label} 고르기`}
            aria-expanded={open}
            onClick={() => setOpen((prev) => !prev)}
            className="shrink-0 w-[1.625rem] h-[1.625rem] rounded-[6px] border border-black/10 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.4)] cursor-pointer"
            style={{ backgroundColor: safeColor }}
          />
          <input
            aria-label={label}
            value={value}
            maxLength={7}
            spellCheck={false}
            onChange={(event) => onChange(event.target.value.toUpperCase())}
            className="flex-1 min-w-0 h-full text-[13px] font-medium tracking-[0.02em] text-[#252525] bg-transparent focus:outline-none"
          />
        </div>
        {open ? (
          <div className={`mall-color-picker absolute top-[calc(100%+0.375rem)] ${popoverAlign === "end" ? "right-0" : "left-0"} z-[60] w-[13.5rem] p-[0.75rem] bg-white border border-[#E5E7EB] rounded-[10px] shadow-[0_8px_24px_rgba(17,24,39,0.12)]`}>
            <style>{`
              .mall-color-picker .react-colorful { width: 100%; height: auto; }
              .mall-color-picker .react-colorful__saturation { height: 140px; border-radius: 8px; border-bottom: none; }
              .mall-color-picker .react-colorful__hue { height: 10px; margin-top: 0.75rem; border-radius: 999px; }
              .mall-color-picker .react-colorful__pointer { width: 16px; height: 16px; border-width: 2px; }
            `}</style>
            <HexColorPicker color={safeColor} onChange={(color) => onChange(color.toUpperCase())} />
            <div className="mt-[0.75rem] pt-[0.75rem] border-t border-[#F0F0F0] grid grid-cols-8 gap-[0.3125rem]">
              {QUICK_COLORS.map((color) => {
                const selected = color === value.toUpperCase();
                return (
                  <button
                    key={color}
                    type="button"
                    aria-label={`${color} 선택`}
                    onClick={() => onChange(color)}
                    className={`aspect-square rounded-[5px] border border-black/10 transition-shadow ${
                      selected ? "ring-2 ring-[#4285F4] ring-offset-1" : "hover:ring-2 hover:ring-[#D1D5DB] hover:ring-offset-1"
                    }`}
                    style={{ backgroundColor: color }}
                  />
                );
              })}
            </div>
          </div>
        ) : null}
      </div>
      {!isValid ? <div className="text-[12px] text-[#E5484D]">#RRGGBB 형식으로 입력해 주세요</div> : null}
    </Field>
  );
};

interface ToggleRowProps {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

export const ToggleRow = ({ label, description, checked, onChange }: ToggleRowProps) => (
  <div className="flex flex-row items-center gap-[0.75rem]">
    <div className="flex-1 min-w-0">
      <div className="text-[13px] text-[#252525]">{label}</div>
      {description ? <div className="text-[12px] text-[#999]">{description}</div> : null}
    </div>
    <ToggleSwitch checked={checked} onChange={onChange} label={label} />
  </div>
);

export const Notice = ({ children, tone = "info" }: { children: React.ReactNode; tone?: "info" | "warn" }) => (
  <div
    className={`px-[0.75rem] py-[0.625rem] rounded-[8px] text-[12px] leading-[1.6] ${
      tone === "warn" ? "bg-[#FFF4E5] text-[#B4560A]" : "bg-[#F5F8FF] text-[#2C5AA0]"
    }`}
  >
    {children}
  </div>
);

/** 섹션 제목 · 부제 (전체 상품 · 포트폴리오 · 상품 리스트 공통) */
export const SectionTextFields = ({
  title,
  subtitle,
  titlePlaceholder,
  subtitlePlaceholder = "비워 두면 부제를 표시하지 않습니다",
  onChange,
}: {
  title: string | null | undefined;
  subtitle: string | null | undefined;
  titlePlaceholder: string;
  subtitlePlaceholder?: string;
  onChange: (patch: { title?: string | null; subtitle?: string | null }) => void;
}) => (
  <div className="grid grid-cols-1 gap-[0.625rem]">
    <Field label="제목">
      <TextField value={title ?? ""} placeholder={titlePlaceholder} onChange={(event) => onChange({ title: event.target.value || null })} />
    </Field>
    <Field label="부제">
      <TextField
        value={subtitle ?? ""}
        placeholder={subtitlePlaceholder}
        onChange={(event) => onChange({ subtitle: event.target.value || null })}
      />
    </Field>
  </div>
);

/** 끌어서 순서를 바꾸는 손잡이 아이콘 */
export const DragHandleIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
    <circle cx="9" cy="6" r="1.5" fill="currentColor" stroke="none" />
    <circle cx="15" cy="6" r="1.5" fill="currentColor" stroke="none" />
    <circle cx="9" cy="12" r="1.5" fill="currentColor" stroke="none" />
    <circle cx="15" cy="12" r="1.5" fill="currentColor" stroke="none" />
    <circle cx="9" cy="18" r="1.5" fill="currentColor" stroke="none" />
    <circle cx="15" cy="18" r="1.5" fill="currentColor" stroke="none" />
  </svg>
);
